import { Loading } from "@/components";
import Modal from "@/components/common/Modal";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsApiLoading, setIsLoading } from "@/store/appSlice";
import "@/styles/css/user-detail.css";
import { default as API, default as apiClient } from "@/utils/api";
import { USER_STATUS, USER_STATUS_VALUE } from "@/utils/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { toast } from 'react-toastify';

DataTable.use(DT);

type UserInfoType = {
  email: string;
  nickname: string;
  avatarURL: string;
  status: string;
  currentToeicScore: number;
  targetToeicScore: number;
  studyPurpose: string;
  trouble: string;
  studyhours: string;
};

type QuestionPerDay = {
  day: string;
  D: number;
  words: number;
  phrases: number;
};

type LearningData = {
  id: string;
  type: string;
  course: string;
  lesson: string;
  correctRate: string;
  studyTime: string;
  courseId?: string;
  lessonId?: string;
  partId?: string;
};

type StudyDetail = {
  studyTime: string;
  studyDuration: string;
  lessonDisplayName?: string;
  questions: {
    questionNumber: number;
    isCorrect: boolean;
    userAnswer: string;
    correctAnswer: string;
    question: string;
  }[];
};

const updateUserSchema = yup.object().shape({
  nickname: yup.string().required("ニックネームが必須です。"),
  email: yup
    .string()
    .matches(
      /^(?=.{1,64}$)[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/,
      "メールアドレスが無効です。"
    )
    .required("メールアドレスが必須です。"),
  status: yup.string().required("ステータスが必須です。"),
});

const UserDetail = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<UserInfoType>(null);
  const [questionPerDay, setQuestionPerDay] = useState<QuestionPerDay[]>([]);
  const [learningData, setLearningData] = useState<LearningData[]>([]);
  const [activeRecord, setActiveRecord] = useState<LearningData | null>(null);
  const [studyDetail, setStudyDetail] = useState<StudyDetail | null>(null);
  const [showAll, setShowAll] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(updateUserSchema),
    defaultValues: userInfo,
  });

  const fetchUserInfo = async () => {
    dispatch(setIsLoading(true));
    try {
      const response = await apiClient.get(`users/${id}`);
      const { data } = response.data;
      setUserInfo(data);
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const fetchQuestionPerDay = async () => {
    try {
      const response = await apiClient.get(`questions-per-day/${id}`);
      const { data } = response.data;
      setQuestionPerDay(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStudyData = async () => {
    try {
      const response = await apiClient.get(`study-data/${id}`);
      const { data } = response.data;
      setLearningData(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStudyDetail = async (userId: string, resultId: string) => {
    try {
      dispatch(setIsLoading(true));
      const response = await apiClient.get(`study-detail/${userId}/${resultId}`);
      const { data } = response.data;
      setStudyDetail(data);
    } catch (err) {
      console.error("Error fetching study detail:", err);
      setStudyDetail(null);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleViewDetail = async (record: LearningData) => {
    setActiveRecord(record);
    if (record.id && id) {
      await fetchStudyDetail(id, record.id);
    }
  };

  const handleUpdateUser = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));
      await API.put(`users/${id}`, {
        ...data,
        status: USER_STATUS_VALUE[data.status],
      });
      setUserInfo({
        ...userInfo,
        ...data,
      });
      toast.success("ユーザが変更されました。");
      document.querySelector<HTMLButtonElement>("#modalUserDetail > div > div > div.modal-header > button")?.click();
    } catch (err) {
      console.error("ユーザーの更新に失敗しました。もう一度お試しください。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleDeleteUser = async () => {
    try {
      if (window.confirm("本当に削除しますか？")) {
        dispatch(setIsApiLoading(true));
        await API.delete(`users/${id}`);
        window.location.href = "/users";
      }
    } catch (err) {
      console.error("ユーザーの削除に失敗しました！");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleBack = () => {
    navigate(-1);
  }

  useEffect(() => {
    if (id) {
      fetchUserInfo();
      fetchQuestionPerDay();
      fetchStudyData();
    }
  }, [id]);

  useEffect(() => {
    if (userInfo) {
      reset({
        nickname: userInfo.nickname || "",
        email: userInfo.email || "",
        status: userInfo.status || "",
      });
    }
  }, [userInfo, reset]);

  return (
    <>
      <Helmet>
        <title>ユーザー詳細</title>
      </Helmet>
      <div className="container-fluid" id="user-detail">
        <div className="row">
          <div className="col-12">
            <div className="form-search user_info row g-2 align-items-center">
              <div className="info-form">
                <Loading />
                {!loading && (
                  <div className="info-row">
                    <div className="info-avatar">
                      <img
                        src={userInfo?.avatarURL || "/images/avatar.jpg"}
                        alt="Avatar"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (target.src !== "/images/avatar.jpg") {
                            target.src = "/images/avatar.jpg";
                          }
                        }}
                      />
                    </div>
                    <div className="info-column">
                      <div className="d-flex align-items-center">
                        <p className="width-110">ユーザーID</p>:&nbsp;&nbsp;<p>{id}</p>
                      </div>
                      <div className="d-flex align-items-center">
                        <p className="width-110">ニックネーム</p>:&nbsp;&nbsp;<p>{userInfo?.nickname}</p>
                      </div>
                      <div className="d-flex align-items-center">
                        <p className="width-110">メールアドレス</p>:&nbsp;&nbsp;<p>{userInfo?.email}</p>
                      </div>
                    </div>
                    <div className="info-column">
                      <div className="d-flex align-items-center">
                        <p className="width-110">現在スコア</p>:&nbsp;&nbsp;<p>{userInfo?.currentToeicScore}</p>
                      </div>
                      <div className="d-flex align-items-center">
                        <p className="width-110">目標スコア</p>:&nbsp;&nbsp;<p>{userInfo?.targetToeicScore}</p>
                      </div>
                      <div className="d-flex align-items-center">
                        <p className="width-110">学習目的</p>:&nbsp;&nbsp;<p>{userInfo?.studyPurpose}</p>
                      </div>
                    </div>
                    <div className="info-column">
                      <div className="d-flex align-items-center">
                        <p className="width-110">学習の困り</p>:&nbsp;<p>{userInfo?.trouble}</p>
                      </div>
                      <div className="d-flex align-items-center">
                        <p className="width-110">学習時間</p>:&nbsp;<p>{userInfo?.studyhours}</p>
                      </div>
                      <div className="d-flex align-items-center">
                        <p className="width-110">ステータス</p>:&nbsp;<p>{userInfo?.status}</p>
                      </div>
                      <div className="text-center">
                        <button
                          className="btn_submit btn btn-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#modalUserDetail"
                        >
                          編集
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-12 wrapp_detail">
            <div className="box_problem col-sm-4">
              <div className="card shadow-sm">
                <div className="card-header text-center bg-light">
                  <strong>日別の問題数</strong>
                </div>
                <div className="card-body">
                  <table className="table table-striped table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>日付</th>
                        <th>D</th>
                        <th>単語</th>
                        <th>熟語</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questionPerDay.length > 0 &&
                        questionPerDay
                          .slice(0, showAll ? questionPerDay.length : 5)
                          .map((item) => (
                            <tr key={item.day}>
                              <td>{item.day}</td>
                              <td>{item.D}</td>
                              <td>{item.words}</td>
                              <td>{item.phrases}</td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer text-center">
                  {!showAll && (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => setShowAll(true)}
                    >
                      さらに見る
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="col-sm-8">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <div
                      id="example3_wrapper"
                      className="dataTables_wrapper no-footer"
                    >
                      <DataTable
                        data={learningData}
                        className="display"
                        options={{
                          rowCallback: (row, data) => {
                            (row as HTMLTableRowElement).onclick = () =>
                              handleViewDetail(data as LearningData);
                          },
                          autoWidth: true,
                          columnDefs: [
                            { targets: "_all", className: "text-start" },
                          ],
                          stripeClasses: [],
                          order: [],
                          searching: false,
                          destroy: true,
                          info: false,
                          paging: true,
                          language: {
                            lengthMenu: "レコード数&nbsp;_MENU_",
                            emptyTable: "データがありません。",
                          },
                          layout: {
                            topStart: null,
                            topEnd: null,
                            bottomStart: "pageLength",
                            bottomEnd: "paging",
                          },
                        }}
                        columns={[
                          {
                            title: "学習形式",
                            data: "type",
                            orderable: true,
                            className: "text-start",
                          },
                          {
                            title: "コース",
                            data: "course",
                            className: "text-start",
                            orderable: true,
                          },
                          {
                            title: "レッスン",
                            data: "lesson",
                            className: "text-start",
                            orderable: true,
                          },
                          {
                            title: "正解率",
                            data: "correctRate",
                            className: "text-start",
                            orderable: true,
                          },
                          {
                            title: "学習時刻",
                            data: "studyTime",
                            className: "text-start",
                            orderable: true,
                          },
                          {
                            orderable: false,
                            data: null,
                            render: (data, type, row) => {
                              return `<div className="d-flex justify-content-center align-items-center">
							                  <a data-bs-toggle="modal" data-bs-target="#modalViewUserDetail" href="#" className="btn-xs sharp me-1"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="feather feather-eye"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg></a>
							                </div>`;
                            },
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-center">
                <div className="btn btn-primary p-2 mb-5" onClick={handleBack}>
                  <i className="fas fa-arrow-left" />
                  &nbsp;&nbsp;&nbsp;一覧に戻る
                </div>
              </div>
            </div>
          </div>
        </div>
        <Modal
          id="modalViewUserDetail"
          title={
            <>
              <h4 className="modal-title fw-bold">学習結果</h4>
              <p>{userInfo?.nickname}</p>
            </>
          }
        >
          <div className="content_detail_user_popup">
            <div className="container_sub">
              <div className="info-section">
                <div className="d-flex align-items-center">
                  <p className="width-75">学習形式</p>:&nbsp;&nbsp;<p>{activeRecord?.type}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="width-75">コース</p>:&nbsp;&nbsp;<p>{activeRecord?.course}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="width-75">レッスン</p>:&nbsp;&nbsp;<p>{studyDetail?.lessonDisplayName || activeRecord?.lesson}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="width-75">正解数</p>:&nbsp;&nbsp;<p>{activeRecord?.correctRate}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="width-75">学習時間</p>:&nbsp;&nbsp;<p>{studyDetail?.studyTime}～{studyDetail?.studyTime} （{studyDetail?.studyDuration}）</p>
                </div>
                <hr />
              </div>
              <div className="questions-section scrollable">
                {studyDetail?.questions?.map((question, index) => (
                  <div key={index} className="question">
                    <p>
                      <i className={`fa-solid ${question.isCorrect ? 'fa-check correct_icon' : 'fa-x wrong_icon'}`}></i>
                      {question.questionNumber}: {question.question}
                    </p>
                    {!question.isCorrect && question.correctAnswer && (
                      <p>
                        <strong>正解：</strong>{question.correctAnswer}
                      </p>
                    )}
                  </div>
                )) || (
                  <div className="question">
                    <p>データがありません。</p>
                  </div>
                )}
              </div>
            </div>
            <div className="btn_group">
              <button
                type="button"
                className="btn btn-secondary btnClose"
                data-bs-dismiss="modal"
              >
                閉じる
              </button>
            </div>
          </div>
        </Modal>
        <Modal
          id="modalUserDetail"
          title={
            <>
              <h5 className="modal-title">基本情報編集</h5>
              <p>ユーザーID:&nbsp;{id}</p>
            </>
          }
          onClose={() => {
            reset(userInfo);
          }}
        >
          <div className="content_detail_user_popup">
            <Form onSubmit={handleSubmit(handleUpdateUser)}>
              <Form.Group className="form-group">
                <Form.Label>ニックネーム</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="ニックネーム"
                  isInvalid={!!errors.nickname}
                  {...register("nickname")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.nickname?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="form-group">
                <Form.Label>メールアドレス</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="メールアドレス"
                  isInvalid={!!errors.email}
                  {...register("email")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="form-group">
                <Form.Label>ステータス</Form.Label>
                <Form.Select
                  isInvalid={!!errors.status}
                  defaultValue={userInfo?.status || "1"}
                  required
                  {...register("status")}
                >
                  {USER_STATUS.map((item) => {
                    return (
                      <option value={item.value} key={item.value}>
                        {item.label}
                      </option>
                    );
                  })}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.status?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <div className="btn_group">
                <button
                  type="submit"
                  className="btn btnStand btn_save btn-primary"
                >
                  保存
                </button>
                <button
                  type="button"
                  onClick={handleDeleteUser}
                  className="btn btnStand btn_del btn-danger"
                >
                  アカウント削除
                </button>
              </div>
            </Form>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default UserDetail;
